import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Breadcrumbs,
  Grid,
  Typography,
  Box,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Select,
  OutlinedInput,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import FooterButtons from "../../common/FooterButtons/FooterButtons";
import "./AddInsServices.css";
import SubscriptionAddInSection from "./SubscriptionAddInSection";
import { useFormik } from "formik";
import {
  AddInSchema,
  AddInsStep1ValidationSchema,
  AddInsStep2ValidationSchema,
} from "../../common/ValidationSchema/AddInsSchema";
import ApiServices from "../../services/ApiServices";
import { useLoading } from "../../hooks/useLoader";
import {
  ADDIN,
  // ADDIN_PUBLISH,
  MASTER_DATA,
  VENDOR_INTEGRATIONSOLUTION_DROPDOWN,
  VENDOR_DROPDOWN,
  ENTERPRISE_GROUP_USERS,
} from "../../constants/api-constants";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { useAddInsContext } from "./AddIns.context";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  // PermissionEnum,
  PermissionService,
} from "../../constants/permissionService";
import { MenuListItems } from "../../common/Sidebar/MenuListItems";
import { SystemModulesEnum } from "../../constants/SystemUsers";
import { formatDate } from "../../utilities/formatter";
import {
  formatSubscriptionsForPayload,
  parseSubscriptionsFromResponse,
} from "./subscriptionUtils";

/*istanbul ignore next */
const AddEditServices = (props) => {
  const history = useHistory();
  const { showLoader } = useLoading();
  const { id } = useParams();
  const isCopyMode =
    new URLSearchParams(window.location.search).get("mode") === "copy";
  const addInsContext = useAddInsContext();
  const [expanded, setExpanded] = React.useState("panel1");
  const [isEditMode, setIsEditMode] = useState(addInsContext.isEditMode);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [integrationSolutionOptions, setIntegrationSolutionOptions] = useState(
    [],
  );
  const [allIntegrationSolutions, setAllIntegrationSolutions] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [hasActiveSubscriptions, setHasActiveSubscriptions] = useState(false);
  const [originalAddInType, setOriginalAddInType] = useState(null);
  const [addInTypeDropdown, setAddInTypeDropdown] = useState([]);
  const [isLoadingSolutions, setIsLoadingSolutions] = useState(false);
  const [enterpriseGroupOptions, setEnterpriseGroupOptions] = useState([]);
  const [isSubscriptionValid, setIsSubscriptionValid] = useState(true);
  const [step1Valid, setStep1Valid] = useState(false);
  const [step2Valid, setStep2Valid] = useState(false);


  const ADDIN_TYPE_OPTIONS = [
    { value: 1, label: "Individual" },
  ];

  const APPLICABLE_TO_OPTIONS = [
    { value: 1, label: "Subscriber" },
    { value: 2, label: "Enterprise" },
  ];

  const formik = useFormik({
    initialValues: {
      addInId: 0,
      addInType: 1,
      vendorId: null,
      integrationSolutionId: null,
      name: "",
      description: "",
      applicableTo: [],
      status: 1,
      isPublished: false,
      isDraft: true,
      subscriptionDetails: {
        costType: 1,
        subscriberPlans: [],
        enterprisePlans: [],
      },
      createdOn: null,
      createdByName: "",
      updatedOn: null,
      updatedByName: "",
    },
    enableReinitialize: true,
    validationSchema: AddInSchema,
  });

  // Helper functions for step-wise validation (moved after formik definition)
  const validateStep1 = async () => {
    try {
      const step1Values = {
        addInType: formik.values.addInType,
        vendorId: formik.values.vendorId,
        integrationSolutionId: formik.values.integrationSolutionId,
        name: formik.values.name,
        description: formik.values.description,
        applicableTo: formik.values.applicableTo,
      };
      await AddInsStep1ValidationSchema.validate(step1Values, {
        abortEarly: false,
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  // Helper to apply publish/draft flags to payload (extracted to reduce complexity)
  const applyPublishFlagsToPayload = (payload, shouldPublish) => {
    if (shouldPublish) {
      payload.isPublished = true;
      payload.isDraft = false;
    } else {
      payload.isPublished = false;
      payload.isDraft = true;
    }
    return payload;
  };

  // Helper to determine success message after update
  const getUpdateSuccessMessage = (currentlyPublished, shouldPublish) => {
    if (shouldPublish) return "Add-in published successfully";
    if (currentlyPublished) return "Draft version created successfully";
    return "Add-in details updated successfully";
  };

  const validateStep2 = async () => {
    try {
      // For free add-ins, step 2 is always valid (costType: 1 = free)
      if (formik.values.subscriptionDetails?.costType === 1) {
        return true;
      }

      // If no applicable audience is selected, step 2 is invalid
      if (
        !formik.values.applicableTo ||
        formik.values.applicableTo.length === 0
      ) {
        return false;
      }

      const step2Values = {
        subscriptionDetails: formik.values.subscriptionDetails,
      };
      const validationContext = {
        applicableTo: formik.values.applicableTo,
      };
      await AddInsStep2ValidationSchema.validate(step2Values, {
        abortEarly: false,
        context: validationContext,
      });
      return true;
    } catch (error) {
      // Log specific field errors
      if (error.inner && error.inner.length > 0) {
        error.inner.forEach((innerError, index) => {
          // Field validation error handling
        });
      }

      return false;
    }
  };

  // Validate steps when relevant fields change
  useEffect(() => {
    const checkStep1Validation = async () => {
      const isValid = await validateStep1();
      setStep1Valid(isValid);
    };
    checkStep1Validation();
  }, [
    formik.values.addInType,
    formik.values.vendorId,
    formik.values.integrationSolutionId,
    formik.values.name,
    formik.values.description,
    formik.values.applicableTo,
  ]);

  useEffect(() => {
    const checkStep2Validation = async () => {
      const isValidStep2 = await validateStep2();

      // For step 2 to be valid, both schema validation and subscription validation must pass
      const finalValidation = isValidStep2 && isSubscriptionValid;
      setStep2Valid(finalValidation);
    };
    checkStep2Validation();
  }, [
    formik.values.subscriptionDetails,
    formik.values.subscriptionDetails?.costType,
    formik.values.applicableTo,
    isSubscriptionValid,
  ]);

  // Initial validation on component mount
  useEffect(() => {
    const runInitialValidation = async () => {
      const step1Valid = await validateStep1();
      const step2Valid = await validateStep2();
      setStep1Valid(step1Valid);
      setStep2Valid(step2Valid && isSubscriptionValid);
    };
    runInitialValidation();
  }, []);

  useEffect(() => {
    if (id) {
      fetchAddInDetails();
    } else {
      // Ensure subscription details are properly initialized for new add-ins
      formik.setFieldValue("subscriptionDetails", {
        costType: 1,
        subscriberPlans: [],
        enterprisePlans: [],
      });
    }
    fetchVendorOptions();
    fetchEnterpriseGroups();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await ApiServices.httpGet(
        `${MASTER_DATA}?include=enumMasterData`,
      );
      setAddInTypeDropdown(response.result.enumMasterData.addInType);
    };
    fetchData();
  }, []);

  const normalizedAddInTypeOptions = (
    Array.isArray(addInTypeDropdown) ? addInTypeDropdown : []
  )
    .map((option) => {
      if (option?.value !== undefined && option?.label !== undefined) {
        return { value: option.value, label: option.label };
      }

      return {
        value: option?.id ?? option?.value ?? option?.code,
        label: option?.name ?? option?.label ?? option?.description,
      };
    })
    .filter((option) => option.value !== undefined && option.label);

  useEffect(() => {
    if (formik.values.vendorId) {
      fetchIntegrationSolutions(formik.values.vendorId);
    } else {
      setIntegrationSolutionOptions([]);
      formik.setFieldValue("integrationSolutionId", null);
    }
  }, [formik.values.vendorId]);

  useEffect(() => {
    if (
      formik.values.integrationSolutionId &&
      allIntegrationSolutions.length > 0
    ) {
      const selectedSolution = allIntegrationSolutions.find(
        (sol) =>
          sol.integrationSolutionId === formik.values.integrationSolutionId,
      );
      if (selectedSolution) {
        if (!formik.values.addInId || formik.values.isDraft || isEditMode) {
          formik.setFieldValue("name", selectedSolution.name || "");
          formik.setFieldValue(
            "description",
            selectedSolution.description || "",
          );
        }
      }
    }
  }, [formik.values.integrationSolutionId, allIntegrationSolutions]);

  const fetchAddInDetails = async () => {
    try {
      showLoader(true);
      const response = await ApiServices.httpGet(`${ADDIN}/${id}`);
      showLoader(false);
      if (response.responseStatus.isSuccess) {
        const data = response.result;
        setIsPublished(data.publishStatus === 1);
        setHasActiveSubscriptions(data.hasActiveSubscriptions || false);
        setOriginalAddInType(data.addInType);

        // Parse subscriptions from API response
        const parsedSubscriptions = parseSubscriptionsFromResponse(
          data.subscriptions || [],
        );

        if (isCopyMode) {
          setIsPublished(false);
          setHasActiveSubscriptions(false);
          setOriginalAddInType(null);
          setIsEditMode(true);
          addInsContext.setEditMode(true);

          formik.setValues({
            ...data,
            addInId: 0,
            publishAddInId: 0,
            publishStatus: 0,
            status: 1,
            isPublished: false,
            isDraft: true,
            isEditAction: false,
            createdOn: null,
            createdByName: "",
            updatedOn: null,
            updatedByName: "",
            description: data.addInDescription ?? "",
            applicableTo: data.applicableTo || [],
            subscriptionDetails: parsedSubscriptions,
          });
          return;
        }

        formik.setValues({
          ...data,
          description: data.addInDescription ?? "",
          applicableTo: data.applicableTo || [],
          subscriptionDetails: parsedSubscriptions,
        });
      } else {
        toast.error(response.responseStatus.error);
      }
    } catch (e) {
      showLoader(false);
      console.error("Error fetching add-in details:", e);
      // Initialize with default subscription details on error
      formik.setFieldValue("subscriptionDetails", {
        costType: 1,
        subscriberPlans: [],
        enterprisePlans: [],
      });
      toast.error("Internal server error");
    }
  };

  const fetchVendorOptions = async () => {
    try {
      const response = await ApiServices.httpGet(VENDOR_DROPDOWN);
      if (response.responseStatus.isSuccess) {
        const vendors = Array.isArray(response.result) ? response.result : [];
        setVendorOptions(
          vendors
            .map((vendor) => ({
              vendorId: vendor?.vendorId ?? vendor?.id ?? vendor?.value,
              name: vendor?.name ?? vendor?.label ?? vendor?.displayName,
            }))
            .filter((vendor) => vendor.vendorId !== undefined && vendor.name),
        );
      }
    } catch (e) {
      console.error("Error fetching vendors:", e);
    }
  };

  const fetchEnterpriseGroups = async () => {
    try {
      const queryObj = {
        searchText: "",
        PageNumber: 1,
        PageSize: 50,
        SortType: "asc",
        SortColumn: "name",
        IsSearchInAllQuery: true,
        filter: {},
      };
      const url = `${ENTERPRISE_GROUP_USERS}?query=${JSON.stringify(queryObj)}`;
      const response = await ApiServices.httpGet(url);
      if (response.responseStatus.isSuccess) {
        const records = response.result?.records || response.result || [];
        const groups = Array.isArray(records) ? records : [];
        const mappedGroups = groups
          .map((group) => ({
            value: group?.userId ?? 0,
            label: group?.brandName ?? group?.name ?? group?.label,
          }))
          .filter((group) => group.value !== undefined && group.label);
        // Add "All" option at the beginning when no search
        setEnterpriseGroupOptions([
          { value: 0, label: "All Enterprise Groups" },
          ...mappedGroups,
        ]);
      }
    } catch (e) {
      console.error("Error fetching enterprise groups:", e);
    }
  };

  // Search enterprise groups by query
  const searchEnterpriseGroups = useCallback(async (searchText) => {
    try {
      const queryObj = {
        searchText: searchText || "",
        PageNumber: 1,
        PageSize: 50,
        SortType: "asc",
        SortColumn: "name",
        IsSearchInAllQuery: true,
        filter: {},
      };
      const url = `${ENTERPRISE_GROUP_USERS}?query=${JSON.stringify(queryObj)}`;
      const response = await ApiServices.httpGet(url);
      if (response.responseStatus.isSuccess) {
        const records = response.result?.records || response.result || [];
        const groups = Array.isArray(records) ? records : [];
        const mappedGroups = groups
          .map((group) => ({
            value: group?.userId ?? 0,
            label: group?.brandName ?? group?.name ?? group?.label,
          }))
          .filter((group) => group.value !== undefined && group.label);
        // Return only search results without "All" option when searching
        return mappedGroups;
      }
      return [];
    } catch (e) {
      console.error("Error searching enterprise groups:", e);
      return [];
    }
  }, []);

  const fetchIntegrationSolutions = async (vendorId) => {
    try {
      setIsLoadingSolutions(true);
      const url = VENDOR_INTEGRATIONSOLUTION_DROPDOWN.replace(
        "{vendorId}",
        vendorId,
      );
      const response = await ApiServices.httpGet(url);
      setIsLoadingSolutions(false);
      if (response.responseStatus.isSuccess) {
        const solutions = Array.isArray(response.result) ? response.result : [];
        const normalizedSolutions = solutions
          .map((solution) => ({
            integrationSolutionId:
              solution?.integrationSolutionId ??
              solution?.id ??
              solution?.value,
            name: solution?.name ?? solution?.label ?? solution?.displayName,
            description: solution?.description ?? "",
            status: solution?.status,
            isAssociatedWithAddIn: solution?.isAssociatedWithAddIn || false,
          }))
          .filter(
            (solution) =>
              solution.integrationSolutionId !== undefined && solution.name,
          );

        setAllIntegrationSolutions(normalizedSolutions);
        setIntegrationSolutionOptions(normalizedSolutions);

        if (normalizedSolutions.length === 0 && !id) {
          toast.info("No Integration Solutions available for this Vendor.");
        }
      }
    } catch (e) {
      setIsLoadingSolutions(false);
      console.error("Error fetching integration solutions:", e);
    }
  };

  const validateBeforeSave = () => {
    // Check if Add-in name already exists
    if (!formik.values.name.trim()) {
      toast.error("Add-in Name is required");
      return false;
    }
    return true;
  };

  const addAddInDetails = async (isNext, shouldPublish = false) => {
    try {
      if (!validateBeforeSave()) {
        return;
      }

      const payload = {
        addInType: formik.values.addInType,
        vendorId: formik.values.vendorId,
        integrationSolutionId: formik.values.integrationSolutionId,
        name: formik.values.name,
        addInDescription: formik.values.description,
        applicableTo: formik.values.applicableTo,
        costType: formik.values.subscriptionDetails?.costType ?? 1,
        subscriptions: formatSubscriptionsForPayload(
          formik.values.subscriptionDetails,
          formik.values.addInId || 0,
        ),
        isPublished: shouldPublish,
        isDraft: !shouldPublish,
      };

      // Add publish-specific properties when publishing
      if (shouldPublish) {
        payload.publishStatus = 1;
        payload.isEditAction = false; // Creating new AddIn
      }

      showLoader(true);
      const response = await ApiServices.httpPost(ADDIN, payload);
      showLoader(false);

      if (response.responseStatus.isSuccess) {
        const successMessage = shouldPublish
          ? "Add-in published successfully"
          : "Add-in details saved successfully";
        toast.success(successMessage);

        // Parse subscriptions from API response
        const parsedSubscriptions = parseSubscriptionsFromResponse(
          response.result.subscriptions || [],
        );

        const result = {
          ...response.result,
          description: response.result.addInDescription ?? "",
          subscriptionDetails: parsedSubscriptions,
        };

        formik.setValues(result);

        if (shouldPublish) {
          setIsPublished(true);
        }

        if (isNext) {
          // Move to step 2 after successful step 1 save
          setExpanded("subscriptionPanel");
        }
      } else {
        toast.error(response.responseStatus.error);
      }
    } catch (e) {
      showLoader(false);
      console.error("Error in addAddInDetails:", e);
      // Ensure subscription details are initialized on error
      if (!formik.values.subscriptionDetails) {
        formik.setFieldValue("subscriptionDetails", {
          costType: 1,
          subscriberPlans: [],
          enterprisePlans: [],
        });
      }
      toast.error("Internal server error");
    }
  };

  const updateAddInDetails = async (isNext, shouldPublish = false) => {
    try {
      if (!validateBeforeSave()) return;

      const { subscriptionDetails, ...restValues } = formik.values;

      const basePayload = {
        ...restValues,
        addInId: formik.values.addInId,
        addInType: formik.values.addInType,
        vendorId: formik.values.vendorId,
        integrationSolutionId: formik.values.integrationSolutionId,
        name: formik.values.name,
        addInDescription: formik.values.description || formik.values.name,
        applicableTo: formik.values.applicableTo,
        costType: formik.values.subscriptionDetails?.costType ?? 1,
        subscriptions: formatSubscriptionsForPayload(
          formik.values.subscriptionDetails,
          formik.values.addInId,
        ),
        status: formik.values.status,
        publishStatus: formik.values.publishStatus || 0,
        isEditAction: formik.values.isEditAction || false,
        isFavorite: formik.values.isFavorite || false,
        publishAddInId: formik.values.publishAddInId || 0,
      };

      const payload = applyPublishFlagsToPayload(basePayload, shouldPublish);

      // Add publish-specific properties when publishing
      if (shouldPublish) {
        payload.publishStatus = 1;
        payload.isEditAction = false; // Editing existing AddIn
      } else {
        payload.isEditAction = true; // Regular update without publishing in edit mode
      }

      showLoader(true);
      const response = await ApiServices.httpPut(ADDIN, payload);
      showLoader(false);

      if (!response.responseStatus.isSuccess) {
        toast.error(response.responseStatus.error);
        return;
      }

      toast.success(getUpdateSuccessMessage(isPublished, shouldPublish));

      // Parse subscriptions from API response
      const parsedSubscriptions = parseSubscriptionsFromResponse(
        response.result.subscriptions || [],
      );

      const result = {
        ...response.result,
        description: response.result.addInDescription ?? "",
        subscriptionDetails: parsedSubscriptions,
      };

      formik.setValues(result);

      if (shouldPublish) setIsPublished(true);

      if (isNext) setExpanded("subscriptionPanel");
      else history.push(`/AddInsList`);
    } catch (e) {
      showLoader(false);
      console.error("Error in updateAddInDetails:", e);
      if (!formik.values.subscriptionDetails) {
        formik.setFieldValue("subscriptionDetails", {
          costType: 1,
          subscriberPlans: [],
          enterprisePlans: [],
        });
      }
      toast.error("Internal server error");
    }
  };

  let moduleId = MenuListItems.find(
    (item) => item.name === SystemModulesEnum.ADDINS,
  )?.moduleId;
  const permissions = PermissionService(moduleId);
  const onCancelHandler = () => {
    setIsEditMode(false);
    history.push(`/AddInsList`);
  };

  const onEditHandler = () => {
    setIsEditMode(true);
  };

  const handleChange = useCallback(
    (panel) => (event, newExpanded) => {
      setExpanded(newExpanded ? panel : false);
    },
    [],
  );

  const handleApplicableToChange = (event) => {
    const {
      target: { value },
    } = event;
    formik.setFieldValue("dirty", true);
    formik.setFieldTouched("applicableTo", true, false);
    formik.setFieldValue(
      "applicableTo",
      typeof value === "string" ? value.split(",") : value,
    );
  };

  // Read-only when published OR draft of a previously published add-in (publishAddInId > 0)
  const isFieldsReadOnly = isPublished || formik.values.publishAddInId > 0;

  const isFieldDisabled = useCallback(
    (fieldName) => {
      if (!isEditMode) return true;

      // When published or draft of published, only description is editable in basic information
      if (isFieldsReadOnly) {
        return fieldName !== "description";
      }

      // Add-in Type cannot be changed after first publish
      if (fieldName === "addInType" && originalAddInType) {
        return true;
      }

      // Applicable To cannot be modified if active subscriptions exist
      if (fieldName === "applicableTo" && hasActiveSubscriptions) {
        return true;
      }

      return false;
    },
    [isEditMode, isFieldsReadOnly, originalAddInType, hasActiveSubscriptions]
  );

  const canPublish = () => {
    return (
      isEditMode &&
      formik.isValid &&
      formik.values.addInType &&
      formik.values.vendorId &&
      formik.values.integrationSolutionId &&
      formik.values.name &&
      formik.values.description &&
      formik.values.applicableTo &&
      formik.values.applicableTo.length > 0 &&
      formik.values.publishStatus !== 1
    );
  };

  // Step-wise button enabling logic
  const canSaveStep1 = () => {
    return isEditMode && step1Valid;
  };

  const canSaveStep2 = () => {
    return isEditMode && step2Valid;
  };

  const handleSubscriptionValidationChange = useCallback((isValid) => {
    setIsSubscriptionValid(isValid);
  }, []);

  const [validateActiveTabFunction, setValidateActiveTabFunction] =
    useState(null);

  const handleValidateActiveTab = useCallback((validateFn) => {
    setValidateActiveTabFunction(() => validateFn);
  }, []);


  const handleStep1Save = () => {
    if (formik.values.addInId) {
      updateAddInDetails(true);
    } else {
      addAddInDetails(true);
    }
  };

  const handleStep2Save = () => {
    if (validateActiveTabFunction) {
      const isActiveTabValid = validateActiveTabFunction();
      if (!isActiveTabValid) {
        toast.error("Please fix the errors in the current tab before saving.");
        return;
      }
    }

    if (formik.values.addInId) {
      updateAddInDetails(false, false); // Save and stay on current form
    } else {
      addAddInDetails(false); // Save and stay on current form
    }
  };

  // Step-specific publish handlers - use same payload as update/save with publish flag
  const handleStep1Publish = () => {
    if (formik.values.addInId) {
      updateAddInDetails(false, true); // Don't move to next step, but publish
    } else {
      addAddInDetails(false, true); // Don't move to next step, but publish
    }
  };

  const handleStep2Publish = () => {
    // Use active tab validation for step 2
    if (validateActiveTabFunction) {
      const isActiveTabValid = validateActiveTabFunction();
      if (!isActiveTabValid) {
        toast.error(
          "Please fix the errors in the current tab before publishing.",
        );
        return;
      }
    }

    if (formik.values.addInId) {
      updateAddInDetails(false, true); // Don't move to next step, but publish
    } else {
      addAddInDetails(false, true); // Don't move to next step, but publish
    }
  };

  return (
    <div className="approval-container">
      <Grid container item xs={12}>
        <Grid item xs={6}>
          <Box pt={0} pr={4} pl={4} pb={0}>
            <h5>Add-Ins/Services</h5>
          </Box>
          <div className="approval-basic-details">
            <div>
              <Breadcrumbs
                separator="›"
                aria-label="breadcrumb"
                className="distributor-breadcrumbs"
              >
                <Link
                  underline="hover"
                  color="inherit"
                  to="/AddInsList"
                  className="breadcrumb-title"
                >
                  Add-Ins/Services
                </Link>
                <Typography color="text.primary">
                  {id ? "Edit Add-In" : "Add New Add-In"}
                </Typography>
              </Breadcrumbs>
            </div>
          </div>
        </Grid>
      </Grid>

      <Grid container spacing={0} paddingLeft={4} marginTop={2}>
        <Grid item md={9} lg={11} sm={12} xs={12}>
          <Box
            p={1}
            className="right-section-vendor"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <div className="parent-div-accord">
              <Accordion
                className="accordion-ent"
                expanded={expanded === "panel1"}
                onChange={handleChange("panel1")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography sx={{ fontWeight: "700" }}>
                    Basic Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="accordion-basic-details">
                  <Grid container spacing={0} m={0}>
                    {/* Add-in Type */}
                    <Grid item xs={3} p={2}>
                      <label htmlFor="addInType">
                        Add-in Type <span className="required-asterisk">*</span>
                      </label>
                      <select
                        id="addInType"
                        name="addInType"
                        value={formik.values.addInType || ""}
                        onChange={(e) => {
                          formik.setFieldValue("dirty", true);
                          formik.setFieldTouched("addInType", true, false);
                          formik.setFieldValue(
                            "addInType",
                            Number(e.target.value),
                          );
                        }}
                        onBlur={formik.handleBlur}
                        className="form-control form-select"
                        disabled={isFieldDisabled("addInType")}
                      >
                        <option value="">Select Add-in Type</option>
                        {(normalizedAddInTypeOptions.length > 0
                          ? normalizedAddInTypeOptions
                          : ADDIN_TYPE_OPTIONS
                        ).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {isPublished && originalAddInType && (
                        <Typography variant="caption" color="text.secondary">
                          Add-in Type cannot be changed after first Publish
                        </Typography>
                      )}
                    </Grid>

                    {/* Vendor */}
                    <Grid item xs={3} p={2}>
                      <label htmlFor="vendorId">
                        Vendor <span className="required-asterisk">*</span>
                      </label>
                      <select
                        id="vendorId"
                        name="vendorId"
                        value={formik.values.vendorId || ""}
                        onChange={(e) => {
                          formik.setFieldValue("dirty", true);
                          formik.setFieldTouched("vendorId", true, false);
                          formik.setFieldValue(
                            "vendorId",
                            Number(e.target.value),
                          );
                          // Reset integration solution when vendor changes
                          formik.setFieldValue("integrationSolutionId", null);
                        }}
                        onBlur={formik.handleBlur}
                        className="form-control form-select"
                        disabled={isFieldDisabled("vendorId")}
                      >
                        <option value="">Select Vendor</option>
                        {vendorOptions.map((vendor) => (
                          <option key={vendor.vendorId} value={vendor.vendorId}>
                            {vendor.name}
                          </option>
                        ))}
                      </select>
                    </Grid>

                    {/* Integration Solution */}
                    <Grid item xs={3} p={2}>
                      <label htmlFor="integrationSolutionId">
                        Integration Solution{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <select
                        id="integrationSolutionId"
                        name="integrationSolutionId"
                        value={formik.values.integrationSolutionId || ""}
                        onChange={(e) => {
                          formik.setFieldValue("dirty", true);
                          formik.setFieldTouched(
                            "integrationSolutionId",
                            true,
                            false,
                          );
                          formik.setFieldValue(
                            "integrationSolutionId",
                            Number(e.target.value),
                          );
                        }}
                        onBlur={formik.handleBlur}
                        className="form-control form-select"
                        disabled={isFieldDisabled("integrationSolutionId")}
                      >
                        <option value="">Select Integration Solution</option>
                        {integrationSolutionOptions.map((solution) => (
                          <option
                            key={solution.integrationSolutionId}
                            value={solution.integrationSolutionId}
                            disabled={solution?.isAssociatedWithAddIn}
                          >
                            {solution.name}
                          </option>
                        ))}
                      </select>
                      {!formik.values.vendorId && (
                        <Typography variant="caption" color="text.secondary">
                          Please select a Vendor first
                        </Typography>
                      )}
                      {formik.values.vendorId &&
                        integrationSolutionOptions.length === 0 &&
                        !isLoadingSolutions && (
                          <Typography variant="caption" color="error">
                            No Integration Solutions available for this Vendor
                          </Typography>
                        )}

                    </Grid>

                    {/* Add-in Name (Read-only, auto-populated) */}
                    <Grid item xs={3} p={2}>
                      <label htmlFor="name">
                        Add-In Name <span className="required-asterisk">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        value={formik.values.name}
                        className="form-label form-control"
                        type="text"
                        disabled={true}
                        style={{ backgroundColor: "#f5f5f5" }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Auto-populated from Integration Solution
                      </Typography>
                    </Grid>

                    {/* Add-in Description */}
                    <Grid item xs={6} p={2}>
                      <label htmlFor="description">
                        Add-In/Service Description{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        value={formik.values.description}
                        onChange={(e) => {
                          formik.setFieldValue("dirty", true);
                          formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        className="form-label form-control"
                        rows={3}
                        placeholder="Enter Description"
                        disabled={isFieldDisabled("description")}
                      />
                      <Typography variant="caption" color="text.secondary">
                        System populates data from Integration Solution. You can
                        edit if required.
                      </Typography>
                    </Grid>

                    {/* Applicable To (Multi-select) */}
                    <Grid item xs={3} p={2}>
                      <label htmlFor="applicableTo">
                        Applicable To{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <FormControl fullWidth>
                        <Select

                          id="applicableTo"
                          multiple
                          value={formik.values.applicableTo}
                          onChange={handleApplicableToChange}
                          onBlur={formik.handleBlur}
                          input={<OutlinedInput />}
                          renderValue={(selected) => {
                            return selected
                              .map(
                                (val) =>
                                  APPLICABLE_TO_OPTIONS.find(
                                    (opt) => opt.value === val,
                                  )?.label,
                              )
                              .join(", ");
                          }}
                          disabled={isFieldDisabled("applicableTo")}
                          className="form-control"
                        >
                          {APPLICABLE_TO_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox
                                checked={formik.values.applicableTo.includes(
                                  option.value,
                                )}
                              />
                              <ListItemText sx={{ fontSize: "1rem" }} primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {hasActiveSubscriptions && (
                        <Typography variant="caption" color="error">
                          This Add-in has active subscriptions. Certain fields
                          cannot be modified.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3} p={2} />

                    <Grid item xs={12} p={0}>
                      <Grid container spacing={0} m={0}>
                        {/* Created Date */}
                        <Grid item xs={3} p={2}>
                          <label htmlFor="createdOn">Created Date</label>
                          <input
                            id="createdOn"
                            name="createdOn"
                            value={
                              formik.values.createdOn
                                ? formatDate(formik.values.createdOn)
                                : ""
                            }
                            className="form-label form-control"
                            type="text"
                            disabled
                          />
                        </Grid>

                        {/* Created By */}
                        <Grid item xs={3} p={2}>
                          <label htmlFor="createdByName">Created By</label>
                          <input
                            id="createdByName"
                            name="createdByName"
                            value={formik.values.createdByName || ""}
                            className="form-label form-control"
                            type="text"
                            disabled
                          />
                        </Grid>

                        {/* Last Updated Date */}
                        <Grid item xs={3} p={2}>
                          <label htmlFor="updatedOn">Last Updated Date</label>
                          <input
                            id="updatedOn"
                            name="updatedOn"
                            value={
                              formik.values.updatedOn
                                ? formatDate(formik.values.updatedOn)
                                : ""
                            }
                            className="form-label form-control"
                            type="text"
                            disabled
                          />
                        </Grid>

                        {/* Last Updated By */}
                        <Grid item xs={3} p={2}>
                          <label htmlFor="updatedByName">Last Updated By</label>
                          <input
                            id="updatedByName"
                            name="updatedByName"
                            value={formik.values.updatedByName || ""}
                            className="form-label form-control"
                            type="text"
                            disabled
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Status Indicator */}
                    {formik.values.addInId > 0 && (
                      <Grid item xs={12} p={2}>
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: isPublished
                              ? "#e8f5e9"
                              : "#fff3e0",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            Status:{" "}
                            {isPublished ? "Published + Active" : "Draft"}
                          </Typography>
                          {isPublished && formik.values.isDraft && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              A draft version exists. Publish to make it active.
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Step 1 Footer Buttons */}
                  <Grid
                    container
                    justifyContent="flex-end"
                    sx={{ mt: 2, mb: 1 }}
                  >
                    <Grid item>
                      <FooterButtons
                        isEditMode={isEditMode}
                        permissions={permissions}
                        permissionId={2}
                        editPermission={formik.values.addInId > 0}
                        showUpdateNext={formik.values.addInId > 0}
                        showSaveNext={!formik.values.addInId}
                        disableUpdateNext={
                          !isEditMode || !canSaveStep1() || !canSaveStep2()
                        }
                        disableSaveNext={
                          !isEditMode || !canSaveStep1() || !canSaveStep2()
                        }
                        showUpdateDetail={formik.values.addInId > 0}
                        showPublishNew={
                          !formik.values.addInId &&
                          formik.values.publishStatus !== 1
                        }
                        disableUpdateDetail={
                          !isEditMode || !canSaveStep1() || !canSaveStep2()
                        }
                        disableSaveDetail={
                          !isEditMode || !canSaveStep1() || !canSaveStep2()
                        }
                        showPublish={formik.values.publishStatus !== 1}
                        disablePublish={!canPublish()}
                        onCancel={onCancelHandler}
                        onEdit={onEditHandler}
                        onUpdateNext={handleStep1Save}
                        onSaveNext={handleStep1Save}
                        onUpdate={() => updateAddInDetails(false)}
                        onSave={() => addAddInDetails(false)}
                        onPublish={handleStep1Publish}
                        onNewPublish={handleStep1Publish}
                        permission={permissions}
                        isPublished={isPublished}
                        currentModule={SystemModulesEnum.ADDINS}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Subscription Details Section */}
              {
                <SubscriptionAddInSection
                  formik={formik}
                  isEditMode={isEditMode}
                  isPublished={isFieldsReadOnly}
                  hasActiveSubscriptions={hasActiveSubscriptions}
                  enterpriseGroupOptions={enterpriseGroupOptions}
                  searchEnterpriseGroups={searchEnterpriseGroups}
                  onValidationChange={handleSubscriptionValidationChange}
                  onValidateActiveTab={handleValidateActiveTab}
                  expanded={expanded}
                  onExpandChange={handleChange}
                  // Step 2 Footer Props
                  showStepFooter={true}
                  stepFooterProps={{
                    isEditMode: isEditMode,
                    permissions: permissions,
                    permissionId: 2,
                    editPermission: formik.values.addInId > 0,

                    disableUpdateNext:
                      !isEditMode || !canSaveStep2() || !canSaveStep1(),
                    disableSaveNext:
                      !isEditMode || !canSaveStep2() || !canSaveStep1(),
                    showUpdateDetail: formik.values.addInId > 0,
                    showPublishNew:
                      !formik.values.addInId &&
                      formik.values.publishStatus !== 1,
                    disableUpdateDetail:
                      !isEditMode || !canSaveStep2() || !canSaveStep1(),
                    disableSaveDetail:
                      !isEditMode || !canSaveStep2() || !canSaveStep1(),
                    showPublish:
                      formik.values.addInId &&
                      formik.values.publishStatus !== 1,
                    disablePublish: !canPublish(),
                    onCancel: onCancelHandler,
                    onEdit: onEditHandler,
                    onUpdate: () => updateAddInDetails(false),
                    onSave: () => handleStep2Save(),
                    onPublish: () => handleStep2Publish(),
                    onNewPublish: () => handleStep2Publish(),
                    permission: permissions,
                    isPublished: isPublished,
                    currentModule: SystemModulesEnum.ADDINS,
                  }}
                />
              }
            </div>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export default AddEditServices;
